import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface SystemSettingsState {
  allowSignups: boolean;
  maintenanceMode: boolean;
  maintenanceMessage: string;
  isLoading: boolean;
}

export const useSystemSettings = () => {
  const [settings, setSettings] = useState<SystemSettingsState>({
    allowSignups: false, // Default to false - signups disabled by default
    maintenanceMode: false,
    maintenanceMessage: 'System is under maintenance. Please check back later.',
    isLoading: true,
  });

  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        // Read canonical self_service flag only
        const [{ data: selfServiceData }, { data: maintenanceData }] = await Promise.all([
          supabase.rpc('get_system_setting', { setting_key: 'self_service' }),
          supabase.rpc('get_system_setting', { setting_key: 'maintenance_mode' }),
        ]);

        // Handle both legacy boolean and new object shapes
        const allowSignups =
          typeof selfServiceData === 'boolean'
            ? selfServiceData
            : Boolean(selfServiceData?.enabled);

        const maintenanceMode =
          typeof maintenanceData === 'boolean'
            ? maintenanceData
            : Boolean(maintenanceData?.enabled);

        const maintenanceMessage =
          typeof maintenanceData === 'object' &&
          maintenanceData !== null &&
          'message' in maintenanceData
            ? (maintenanceData as any).message ||
              'System is under maintenance. Please check back later.'
            : 'System is under maintenance. Please check back later.';

        setSettings({
          allowSignups,
          maintenanceMode,
          maintenanceMessage,
          isLoading: false,
        });
      } catch (error) {
        console.error('Error fetching system settings:', error);
        // Fall back to defaults on error
        setSettings({
          allowSignups: false,
          maintenanceMode: false,
          maintenanceMessage: 'System is under maintenance. Please check back later.',
          isLoading: false,
        });
      }
    };

    fetchSystemSettings();
  }, []);

  return settings;
};
